// Helper functions for conditional content used in:
// * Frontend useModularMarkdown and
// * AidKitTS Speak getWhatToSay
// * AidkitTS NotificationService and notification formatContent

import { evalDistroConditional } from '../eval';
import { RichText, ConditionalContent, Content, Text, ConditionalContentList } from '../survey';
import { CompileExpressionToJS } from '../translation/expr_to_js';
import { getFormulaParents, FIELD_REF_REGEX } from '../util';

export default function buildConditionalContent(props: {
  uid?: string,
  info: Record<string, string | undefined>,
  content: string | Content,
  lang: keyof Text
}) {
  const { content, info, lang } = props;

  const markedPieces: string[] = [];
  if (Array.isArray(content)) {
        type T = RichText | ConditionalContent;
        type C = ConditionalContent;
        let addContent = (content: T) => {
          if ((content as C).conditional) {
            // Map components
            let passing = evalDistroConditional((content as C).conditional, info);
            if (passing) {
              let components = (content as C).components;
              if (Array.isArray(components)) {
                for (const component of components) {
                  addContent(component);
                }
              } else {
                addContent(components);
              }
            } else {
              let otherwise = (content as C).otherwise;
              if (otherwise) {
                if (Array.isArray(otherwise)) {
                  for (const component of otherwise) {
                    addContent(component);
                  }
                } else {
                  addContent(otherwise);
                }
              }
            }
          } else {
            let text = content as RichText;
            markedPieces.push(text[lang] || text.en || '');
          }
        }
        content.forEach(addContent);
  } else {
    markedPieces.push(typeof content === 'string' ? content : (content[lang] || content.en || ''));
  }

  return markedPieces;

}

/**
 * Extracts template variables from content blocks or a string.
 * 
 * Traverses the content structure to find all `$variable_name` references that would 
 * need to be replaced during rendering. For conditional content blocks, it extracts 
 * variables from both the conditional expressions AND from all possible content branches
 * (components and otherwise clauses) to ensure complete variable discovery.
 *
 * @param params - The parameters for the function.
 * @param params.content - The content to extract variables from. Can be a string, RichText object, or ConditionalContentList.
 * @returns An array of unique variable names (without the `$` prefix).
 */
export function extractVariables(params: {
  content: Content | string,
  lang?: keyof Text
}): string[] {
  const content = params.content;

  const infoKeys: string[] = [];

  const extractFromBlock = (block: Text | ConditionalContent | RichText | ConditionalContentList | string) => {
    if (typeof block === 'string') {
      infoKeys.push(...(block.match(FIELD_REF_REGEX) || []).map(v => v.slice(1)));
    } else if (Array.isArray(block)) {
      for (const item of block) {
        extractFromBlock(item);
      }
    } else if ('conditional' in block) {
      const expr = CompileExpressionToJS(block.conditional);
      const parents = getFormulaParents(expr);
      infoKeys.push(...parents);

      const components = block.components;
      const otherwise = block.otherwise || [];

      // Extract variables from all possible branches
      for (const component of components) {
        extractFromBlock(component);
      }
      for (const component of otherwise) {
        extractFromBlock(component);
      }
    } else {
      extractFromBlock(block[params.lang || 'en'] || block.en);
    }
  };

  extractFromBlock(content);

  return [...new Set(infoKeys)];
}
